/**
 * Reorder flat hierarchy nodes (each carrying `parent_id`) into depth-first tree
 * order -- every node immediately follows its parent, siblings keep their
 * original order -- and annotate each with `depth`.
 *
 * Lets a picker show a node's own name indented by depth instead of its full
 * `display_path`, so "Acme Healthcare Organization / Memorial Hospital / ..."
 * doesn't repeat on every row underneath Memorial Hospital.
 */
export function buildHierarchyTree(nodes) {
  const byParent = new Map();
  nodes.forEach((node) => {
    const key = node.parent_id ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(node);
  });

  const ordered = [];
  const visit = (parentId, depth) => {
    (byParent.get(parentId) || []).forEach((node) => {
      ordered.push({ ...node, depth });
      visit(node.id, depth + 1);
    });
  };
  visit(null, 0);

  // A node whose parent_id doesn't resolve to another node in the list (e.g. the
  // parent was filtered out upstream) would otherwise vanish silently -- append
  // it at the root rather than dropping it.
  if (ordered.length < nodes.length) {
    const seen = new Set(ordered.map((n) => n.id));
    nodes.forEach((node) => {
      if (!seen.has(node.id)) ordered.push({ ...node, depth: 0 });
    });
  }

  return ordered;
}
